import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Activity, Edit2, Eye, Users, DollarSign, AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import AdminUnitEditModal from './AdminUnitEditModal';

const STATE_COLORS = {
  DRAFT: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  OPEN: 'bg-[#4ADE80]/20 text-[#4ADE80] border-[#4ADE80]/30',
  PAUSED: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  FUNDED: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  CLOSED: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  EXITED: 'bg-white/20 text-white border-white/30',
};

const formatINR = (val) => {
  if (!val) return '0';
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return `${(val / 1000).toFixed(0)} K`;
};

export default function AdminProjectDetail({ projectId, onBack, onAddUnit, onViewUnitLifecycle, onViewUnitDetail }) {
  const [project, setProject] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUnit, setEditingUnit] = useState(null);

  useEffect(() => {
    fetchProjectUnits();
  }, [projectId]);

  const fetchProjectUnits = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/units`);
      setProject(response.data.project);
      setUnits(response.data.units);
    } catch (error) {
      toast.error('Failed to load project details');
      setProject(null);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  // Aggregate summary
  const summary = units.reduce((acc, unit) => {
    acc.totalInvestors += (unit.investorCount || 0);
    acc.totalRaised += (unit.financial?.capitalRaised || 0);
    acc.totalTarget += (unit.financial?.capitalTarget || 0);
    acc.pendingActions += (unit.pendingActions || 0);
    return acc;
  }, { totalInvestors: 0, totalRaised: 0, totalTarget: 0, pendingActions: 0 });

  if (loading) return <div className="p-10"><p className="text-gray-500">Loading...</p></div>;
  if (!project) return <div className="p-10"><p className="text-gray-500">Project not found</p></div>;

  return (
    <div className="p-10" data-testid="project-detail">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-white mb-6 flex items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> Back to Properties
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-light text-white mb-1">{project.name}</h1>
          <p className="text-sm text-gray-500">{project.location?.city} · {project.rera?.number}</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchProjectUnits} className="p-2 border border-white/10 text-gray-400 hover:text-white" data-testid="refresh-project">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => onAddUnit && onAddUnit(projectId)}
            className="flex items-center gap-2 px-4 py-2 bg-[#4ADE80] text-black font-medium hover:bg-[#4ADE80]/90">
            <Plus className="w-4 h-4" /> Add Unit
          </button>
        </div>
      </div>

      {/* Project Info Row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="border border-white/10 p-4">
          <p className="text-xs text-gray-600 uppercase mb-1">Developer</p>
          <p className="text-sm text-white">{project.developer?.name}</p>
        </div>
        <div className="border border-white/10 p-4">
          <p className="text-xs text-gray-600 uppercase mb-1">Type</p>
          <p className="text-sm text-white capitalize">{project.type}</p>
        </div>
        <div className="border border-white/10 p-4">
          <p className="text-xs text-gray-600 uppercase mb-1">Broker Max</p>
          <p className="text-sm font-mono text-white">{project.brokerage?.brokerShareMax || 0}%</p>
        </div>
      </div>

      {/* Investment Summary Bar */}
      {summary.totalInvestors > 0 && (
        <div className="flex items-center gap-6 mb-6 p-4 border border-white/10 bg-white/[0.02]" data-testid="project-investment-summary">
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="text-gray-500">Total Investors:</span>
            <span className="text-white font-mono">{summary.totalInvestors}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4 text-gray-500" />
            <span className="text-gray-500">Raised:</span>
            <span className="text-[#4ADE80] font-mono">{formatINR(summary.totalRaised)}</span>
            <span className="text-gray-600">/ {formatINR(summary.totalTarget)}</span>
          </div>
          {summary.pendingActions > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-amber-400">
              <AlertTriangle className="w-3.5 h-3.5" />
              {summary.pendingActions} pending actions
            </div>
          )}
        </div>
      )}

      {/* Units */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg text-white">Units ({units.length})</h2>
      </div>

      {units.length === 0 ? (
        <div className="border border-white/10 p-12 text-center">
          <p className="text-gray-500 mb-4">No units added yet</p>
          <button onClick={() => onAddUnit(projectId)} className="text-sm text-[#4ADE80]">Add your first unit</button>
        </div>
      ) : (
        <div className="space-y-3">
          {units.map((unit) => {
            const unitState = unit.lifecycle?.currentState || 'DRAFT';
            return (
              <div key={unit.unitId} className="border border-white/10" data-testid={`unit-card-${unit.unitId}`}>
                <div className="flex items-center justify-between px-5 py-4 hover:bg-white/[0.03] transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-sm text-white font-medium">{unit.unitName}</span>
                    <span className={`inline-block px-2 py-0.5 text-xs border ${STATE_COLORS[unitState] || STATE_COLORS.DRAFT}`}>{unitState}</span>
                    <span className="text-xs text-gray-500">{unit.specifications?.configuration}</span>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="text-right">
                      <p className="text-sm font-mono text-[#4ADE80]">{formatINR(unit.financial?.capitalRaised)}<span className="text-gray-600"> / {formatINR(unit.financial?.capitalTarget)}</span></p>
                      <p className="text-xs text-gray-500">{unit.financial?.fundingPercentage?.toFixed(0) || 0}% funded</p>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <Users className="w-3 h-3" /> {unit.investorCount || 0}
                    </div>
                    {(unit.pendingActions || 0) > 0 && (
                      <span className="text-xs text-amber-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {unit.pendingActions}
                      </span>
                    )}
                    <div className="flex gap-1.5 ml-2">
                      <button onClick={() => onViewUnitDetail && onViewUnitDetail(unit.unitId)}
                        className="px-2.5 py-1 text-xs border border-[#4ADE80]/50 text-[#4ADE80] hover:bg-[#4ADE80]/10 flex items-center gap-1" data-testid={`view-btn-${unit.unitId}`}>
                        <Eye className="w-3 h-3" /> Detail
                      </button>
                      <button onClick={() => setEditingUnit(unit)}
                        className="px-2.5 py-1 text-xs border border-white/10 text-gray-400 hover:text-white hover:border-white/20 flex items-center gap-1" data-testid={`edit-btn-${unit.unitId}`}>
                        <Edit2 className="w-3 h-3" /> Edit
                      </button>
                      <button onClick={() => onViewUnitLifecycle && onViewUnitLifecycle(unit.unitId)}
                        className="px-2.5 py-1 text-xs border border-white/10 text-gray-400 hover:text-white hover:border-white/20 flex items-center gap-1" data-testid={`lifecycle-btn-${unit.unitId}`}>
                        <Activity className="w-3 h-3" /> Lifecycle
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Unit Modal */}
      {editingUnit && (
        <AdminUnitEditModal
          unit={editingUnit}
          onClose={() => setEditingUnit(null)}
          onSaved={() => { fetchProjectUnits(); setEditingUnit(null); }}
        />
      )}
    </div>
  );
}
